import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Eye, 
  Edit3, 
  Shield, 
  UserCheck, 
  UserX, 
  Clock, 
  Calendar,
  Search,
  Filter,
  Loader2,
  AlertTriangle,
  Truck,
  Droplets,
  Sofa,
  Package,
  CheckCircle,
  Home,
  Building2,
  LogOut
} from 'lucide-react';
import { safeFetch } from '../utils/safeFetch';
import { API_URL } from '../config';
import { USER_STAGE_ORDER, STAGES_CONFIG, STAGE_BADGE_STYLES, getStagesFromRoles } from '../utils/stages';
import { useDryCleaningEnabled } from '../utils/featureFlags';
import { UsersListSkeleton } from './Skeleton';
import Dropdown from './Dropdown';
import { formatPersonName, getPersonInitials } from '../utils/personName';
import { haptic } from '../utils/vibration';
import { ConfirmModal } from './ConfirmModal';
import useToast from '../hooks/useToast';
import useModalBackButton from '../hooks/useModalBackButton';

const UsersList = ({ onViewUser, onCreateUser }) => {
  const isDryCleaningEnabled = useDryCleaningEnabled();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [rightsFilter, setRightsFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [logoutTarget, setLogoutTarget] = useState(null);
  const [showLogoutAllConfirm, setShowLogoutAllConfirm] = useState(false);
  const [sessionActionBusy, setSessionActionBusy] = useState(false);
  const { showToast } = useToast();
  const currentUserType = localStorage.getItem('user_type');
  const isDeveloper = currentUserType === 'developer';

  useModalBackButton(!!logoutTarget, () => setLogoutTarget(null));
  useModalBackButton(showLogoutAllConfirm, () => setShowLogoutAllConfirm(false));

  const userTypeLabels = {
    user: 'Пользователь',
    dispatcher: 'Диспетчер',
    admin: 'Администратор',
    developer: 'Разработчик'
  };

  // Получаем доступные опции прав на основе текущего пользователя
  const getAvailableRightsOptions = () => {
    const currentUserType = localStorage.getItem('user_type');
    const allOptions = [
      { value: 'all', label: 'Все права' },
      { value: 'user', label: 'Пользователь' },
      { value: 'dispatcher', label: 'Диспетчер' },
      { value: 'admin', label: 'Администратор' },
      { value: 'developer', label: 'Разработчик' }
    ];
    
    if (currentUserType === 'admin') {
      // Админы не видят разработчиков
      return allOptions.filter(option => option.value !== 'developer');
    }
    
    return allOptions;
  };

  // Конфигурация этапов заказов (используем общие константы)
  const orderStages = Object.fromEntries(
    Object.entries(STAGES_CONFIG).map(([key, config]) => [
      key,
      {
        name: config.label,
        icon: config.icon,
        color: STAGE_BADGE_STYLES[config.color] || STAGE_BADGE_STYLES.blue
      }
    ])
  );

  const hasDeferredOrdersAccess = (user) => {
    const normalizedUserType = String(user?.user_type || '').trim().toLowerCase();
    if (!['dispatcher', 'admin', 'developer'].includes(normalizedUserType)) return false;
    return typeof user?.can_access_deferred_orders === 'boolean'
      ? user.can_access_deferred_orders
      : ['admin', 'developer'].includes(normalizedUserType);
  };

  // Получение доступных этапов пользователя на основе его ролей (по названиям этапов)
  const getUserStages = (user) => {
    const result = [];
    try {
      const userRoles = user?.user_roles;
      const roles = typeof userRoles === 'string' ? JSON.parse(userRoles) : userRoles;
      if (Array.isArray(roles)) {
        const userStageKeys = new Set(getStagesFromRoles(roles));
        USER_STAGE_ORDER
          .filter(key => isDryCleaningEnabled || key !== 'cleaning')
          .filter(key => userStageKeys.has(key))
          .forEach(key => result.push({
            key,
            ...orderStages[key]
          }));
      }
    } catch (error) {
      console.error('Error parsing user roles:', error);
    }

    if (hasDeferredOrdersAccess(user) && orderStages.deferred) {
      result.push({ key: 'deferred', ...orderStages.deferred });
    }
    return result;
  };

  const statusLabels = {
    active: 'Активен',
    blocked: 'Заблокирован'
  };

  const statusColors = {
    active: 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-300',
    blocked: 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-300'
  };

  const userTypeColors = {
    user: 'text-blue-600 bg-blue-100 dark:bg-blue-900 dark:text-blue-300',
    dispatcher: 'text-purple-600 bg-purple-100 dark:bg-purple-900 dark:text-purple-300',
    admin: 'text-orange-600 bg-orange-100 dark:bg-orange-900 dark:text-orange-300',
    developer: 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-300'
  };

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      
      // Для разработчиков при фильтре "deleted" загружаем удаленных пользователей
      let url = `${API_URL}/api/users`;
      if (localStorage.getItem('user_type') === 'developer' && statusFilter === 'deleted') {
        url = `${API_URL}/api/users/deleted`;
      }
      
      const data = await safeFetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setUsers(data.users || []);
    } catch (err) {
      console.error('Ошибка загрузки пользователей:', err);
      setError(err.message || 'Ошибка загрузки пользователей');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem('token');
      const data = await safeFetch(`${API_URL}/api/departments`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setDepartments(data.departments || data || []);
    } catch (err) {
      console.error('Ошибка загрузки подразделений:', err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchDepartments();
  }, [statusFilter]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Никогда';
    const date = new Date(dateString);
    // Отображаем в GMT+3 (Московское время)
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Moscow'
    });
  };

  const getActivityStatus = (lastActivity) => {
    if (!lastActivity) return 'Никогда не заходил';

    const activityDate = new Date(lastActivity);
    const nowMs = Date.now();
    const activityMs = activityDate.getTime();
    const diffMinutes = Math.max(0, Math.floor((nowMs - activityMs) / (1000 * 60)));

    if (diffMinutes < 1) return 'Только что';
    if (diffMinutes < 5) return 'Онлайн';
    if (diffMinutes < 60) return `${diffMinutes} мин назад`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)} ч назад`;
    return `${Math.floor(diffMinutes / 1440)} дн назад`;
  };

  // Фильтрация пользователей
  const filteredUsers = users.filter(user => {
    // Поиск по имени, фамилии, логину и телефону
    const searchMatch = 
      (user.first_name && user.first_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.last_name && user.last_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.login && user.login.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.phone && user.phone.includes(searchTerm));
    
    // Фильтр по статусу (для разработчиков добавляем опцию "deleted")
    let statusMatch = true;
    if (statusFilter !== 'all') {
      if (localStorage.getItem('user_type') === 'developer' && statusFilter === 'deleted') {
        statusMatch = user.user_status === 'deleted';
      } else {
        statusMatch = user.user_status === statusFilter;
      }
    }
    
    // Фильтр по правам
    const rightsMatch = rightsFilter === 'all' || user.user_type === rightsFilter;
    
    // Фильтр по подразделению
    let departmentMatch = departmentFilter === 'all';
    if (departmentFilter !== 'all') {
      try {
        const userDepartments = JSON.parse(user.departments || '[]');
        departmentMatch = userDepartments.includes(parseInt(departmentFilter));
      } catch (e) {
        departmentMatch = false;
      }
    }
    
    return searchMatch && statusMatch && rightsMatch && departmentMatch;
  });

  const endCurrentSession = () => {
    sessionStorage.setItem('auth_notice', 'session-ended');
    localStorage.removeItem('token');
    localStorage.removeItem('user_type');
    localStorage.removeItem('user_id');
    window.dispatchEvent(new CustomEvent('auth-error', {
      detail: { type: 'revoked', message: 'Сессия завершена разработчиком' }
    }));
  };

  const handleRevokeUserTokens = async () => {
    if (!logoutTarget || sessionActionBusy) return;

    const target = logoutTarget;
    setSessionActionBusy(true);
    try {
      await safeFetch(`${API_URL}/api/users/${target.id}/revoke-tokens`, {
        method: 'POST'
      });
      setLogoutTarget(null);

      if (String(localStorage.getItem('user_id') || '') === String(target.id)) {
        endCurrentSession();
        return;
      }

      showToast(`${formatPersonName(target, target.login || `Пользователь #${target.id}`)} должен войти в приложение заново.`, 'success');
    } catch (err) {
      showToast(err.message || 'Не удалось завершить сессии', 'error');
    } finally {
      setSessionActionBusy(false);
    }
  };

  const handleRevokeAllTokens = async () => {
    if (sessionActionBusy) return;

    setSessionActionBusy(true);
    try {
      await safeFetch(`${API_URL}/api/users/revoke-all-tokens`, {
        method: 'POST'
      });
      setShowLogoutAllConfirm(false);
      endCurrentSession();
    } catch (err) {
      showToast(err.message || 'Не удалось завершить все сессии', 'error');
    } finally {
      setSessionActionBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {/* Заголовок с фильтрами skeleton */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <div className="pl-9 pr-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 w-full">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
            </div>
          </div>
          
          <div className="flex gap-2">
            <div className="w-16 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
            <div className="w-16 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
          </div>
        </div>
        
        {/* Заголовок и кнопка создания skeleton */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Users className="w-5 h-5 text-gray-400" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32"></div>
          </div>
          
          <div className="w-20 h-8 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
        </div>
        
        {/* Skeleton списка пользователей */}
        <UsersListSkeleton count={5} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6">
        <div className="flex items-center space-x-3">
          <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
          <div>
            <h3 className="text-red-800 dark:text-red-200 font-medium">Ошибка загрузки</h3>
            <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
          </div>
        </div>
        <button 
          onClick={() => {
            haptic('tap');
            fetchUsers();
          }}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Повторить
        </button>
      </div>
    );
  }

  const handleRestoreUser = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      
      await safeFetch(`${API_URL}/api/users/${userId}/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Обновляем список пользователей
      fetchUsers();
    } catch (err) {
      console.error('Ошибка восстановления пользователя:', err);
      setError(err.message || 'Ошибка восстановления пользователя');
    }
  };

  return (
    <div className="space-y-4">
      {/* Кнопка открытия панели поиска и фильтров */}
      <div className="flex items-center justify-end">
        <button
          onClick={() => {
            haptic(showFilters ? 'close' : 'open');
            setShowFilters((prev) => !prev);
          }}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 shadow-sm transition-colors text-sm"
        >
          <Filter className="w-4 h-4" />
          <span>Поиск и фильтры</span>
        </button>
      </div>

      {showFilters && (
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative min-w-0 flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Поиск по имени, логину или телефону"
              className="w-full rounded-xl border border-gray-300 bg-white py-3 pl-10 pr-4 text-sm text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:placeholder:text-gray-500"
            />
          </div>

          <div className="relative min-w-0 flex-1 sm:flex-none sm:w-64">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <div className="pl-9">
              <Dropdown
                value={statusFilter}
                onChange={(value) => {
                  if (value !== statusFilter) haptic('selection');
                  setStatusFilter(value);
                }}
                options={[
                  { value: 'all', label: 'Все статусы' },
                  { value: 'active', label: 'Активные' },
                  { value: 'blocked', label: 'Заблокированные' },
                  ...(localStorage.getItem('user_type') === 'developer' ? [{ value: 'deleted', label: 'Удаленные' }] : [])
                ]}
                placeholder="Статус"
              />
            </div>
          </div>
          
          <div className="relative min-w-0 flex-1 sm:flex-none sm:w-64">
            <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <div className="pl-9">
              <Dropdown
                value={rightsFilter}
                onChange={(value) => {
                  if (value !== rightsFilter) haptic('selection');
                  setRightsFilter(value);
                }}
                options={getAvailableRightsOptions()}
                placeholder="Права"
              />
            </div>
          </div>
 
          <div className="relative min-w-0 flex-1 sm:flex-none sm:w-64">
            <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <div className="pl-9">
              <Dropdown
                value={departmentFilter}
                onChange={(value) => {
                  if (String(value) !== String(departmentFilter)) haptic('selection');
                  setDepartmentFilter(value);
                }}
                options={[{ value: 'all', label: 'Все подразделения' }, ...departments.map(d => ({ value: d.id, label: d.name }))]}
                placeholder="Подразделение"
              />
            </div>
          </div>
        </div>
      )}

      {/* Заголовок и кнопка создания */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Users className="w-5 h-5 text-blue-600" />
          <span className="text-base font-medium text-gray-900 dark:text-gray-100">
            Пользователи ({filteredUsers.length})
          </span>
        </div>
        
        <div className="flex flex-col items-stretch gap-2">
          <button
            onClick={() => {
              haptic('open');
              onCreateUser();
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 text-sm font-medium shadow-sm"
          >
            <Users className="w-4 h-4" />
            <span>Новый</span>
          </button>
          {isDeveloper && (
            <button
              type="button"
              onClick={() => {
                haptic('warning');
                setShowLogoutAllConfirm(true);
              }}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-700 shadow-sm transition-colors hover:bg-red-50 dark:border-red-900/60 dark:bg-gray-900 dark:text-red-300 dark:hover:bg-red-950/30"
              title="Разлогинить всех пользователей"
            >
              <LogOut className="h-4 w-4" />
              <span>Выйти всем</span>
            </button>
          )}
        </div>
      </div>

      {/* Список пользователей */}
      {filteredUsers.length === 0 ? (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {searchTerm || statusFilter !== 'all' || rightsFilter !== 'all' || departmentFilter !== 'all' ? 'Пользователи не найдены' : 'Нет пользователей'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {searchTerm || statusFilter !== 'all' || rightsFilter !== 'all' || departmentFilter !== 'all'
              ? 'Попробуйте изменить критерии поиска'
              : 'Создайте первого пользователя'}
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filteredUsers.map((user) => (
            <div
              key={user.id}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                      {getPersonInitials(user)}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                        {formatPersonName(user, user.login || 'Без имени')}
                      </h3>
                      <p className="text-xs text-gray-600 dark:text-gray-400">@{user.login}</p>
                    </div>
                  </div>
                  
                  <div className="mb-2">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <div className="flex flex-wrap gap-1">
                        <div className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${userTypeColors[user.user_type]}`}>
                          {userTypeLabels[user.user_type]}
                        </div>
                        <div className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                          user.user_status === 'deleted' 
                            ? 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-300' 
                            : statusColors[user.user_status]
                        }`}>
                          {user.user_status === 'deleted' ? 'Удален' : statusLabels[user.user_status]}
                        </div>
                      </div>

                      <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                        <Clock className="w-3 h-3 mr-1" />
                        <span>{getActivityStatus(user.last_activity)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Этапы доступные пользователю */}
                  {getUserStages(user).length > 0 ? (
                    <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                      <span className="text-gray-500 dark:text-gray-400 text-xs block mb-2">Доступные этапы:</span>
                      <div className="flex flex-wrap gap-1">
                        {getUserStages(user).map((stage) => {
                          const Icon = stage.icon;
                          return (
                            <div
                              key={stage.key}
                              className={`inline-flex items-center px-2 py-1 rounded-lg text-xs ${stage.color}`}
                              title={stage.name}
                            >
                              <Icon className="w-3 h-3 mr-1" />
                              <span className="hidden sm:inline">{stage.name}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                      <span className="text-gray-400 dark:text-gray-500 text-xs italic">Доступных этапов нет</span>
                    </div>
                  )}
                  
                  {user.block_reason && (
                    <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <p className="text-xs text-red-800 dark:text-red-200">
                        <strong>Причина блокировки:</strong> {user.block_reason}
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="flex space-x-1 ml-3">
                  {/* Кнопки действий */}
                  <div className="flex flex-col items-center gap-1">
                    <button
                      onClick={() => {
                        haptic('open');
                        onViewUser(user);
                      }}
                      className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      title="Просмотр"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    {isDeveloper && user.user_status !== 'deleted' && (
                      <button
                        type="button"
                        onClick={() => {
                          haptic('warning');
                          setLogoutTarget(user);
                        }}
                        disabled={sessionActionBusy}
                        className="p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                        title="Разлогинить пользователя"
                        aria-label={`Разлогинить пользователя ${user.login || user.id}`}
                      >
                        <LogOut className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        isOpen={!!logoutTarget}
        title="Завершить сессии пользователя?"
        message={logoutTarget
          ? `${formatPersonName(logoutTarget, logoutTarget.login || `Пользователь #${logoutTarget.id}`)} будет разлогинен на всех устройствах и должен будет войти заново.`
          : ''}
        confirmText={sessionActionBusy ? 'Завершаю…' : 'Разлогинить'}
        cancelText="Отмена"
        onConfirm={handleRevokeUserTokens}
        onCancel={() => !sessionActionBusy && setLogoutTarget(null)}
        type="warning"
      />

      <ConfirmModal
        isOpen={showLogoutAllConfirm}
        title="Разлогинить всех пользователей?"
        message="Все пользователи, включая вас, будут разлогинены на всех устройствах и должны будут войти в приложение заново."
        confirmText={sessionActionBusy ? 'Завершаю…' : 'Разлогинить всех'}
        cancelText="Отмена"
        onConfirm={handleRevokeAllTokens}
        onCancel={() => !sessionActionBusy && setShowLogoutAllConfirm(false)}
        type="danger"
      />


    </div>
  );
};

export default UsersList;
